import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useLocale from 'Hooks/useLocale';
import { CONNECTOR_SERVICES, DOCUMENTATION_LINKS } from 'Utils/constants';
import { formatList } from 'Utils/formatters';

// Services without a connection flow of their own, so they are not in CONNECTOR_SERVICES.
const OTHER_SERVICES_NAMES = {
    mercury: 'Mercury',
    plaid: 'Plaid',
    stripe: 'Stripe',
    teller: 'Teller',
};

// React already escapes the text: i18next escaping would show names and dates as HTML entities.
const NO_ESCAPE = { escapeValue: false };

export const getConnectorServiceName = (service) =>
    CONNECTOR_SERVICES[service]?.name ?? OTHER_SERVICES_NAMES[service] ?? null;

const getConnectorLabel = ({ name, service }) => {
    const serviceName = getConnectorServiceName(service);

    return serviceName ? `${name} (${serviceName})` : name;
};

/**
 * Texts that explain that connectors are down or have problems, the same in the outages banner and
 * in the connections. Each connector is `{ name, service, message, since }`: the title says which
 * connector and since when, and the message written by support replaces the generic explanation.
 */
export default function useConnectorOutageTexts() {
    const { t, i18n } = useTranslation('common');
    const locale = useLocale();

    // "The X connector from Y is down since <day>": the key changes with the status.
    const getTitle = useCallback(
        (titleKey, { name, service, since }) => {
            const serviceName = getConnectorServiceName(service);

            const title = serviceName
                ? t(`provider-connections.${titleKey}`, { name, service: serviceName, interpolation: NO_ESCAPE })
                : t(`provider-connections.${titleKey}-without-service`, { name, interpolation: NO_ESCAPE });

            if (!since) return title;

            const sinceText = t('provider-connections.bank-outage-since', {
                date: since.toLocaleDateString(locale),
                interpolation: NO_ESCAPE,
            });

            return `${title} ${sinceText}`;
        },
        [t, locale],
    );

    const getOutageTexts = useCallback(
        (outages) => {
            const [firstOutage] = outages;
            const isSingle = outages.length === 1;

            const title = isSingle
                ? getTitle('bank-outage-title', firstOutage)
                : t('provider-connections.bank-outage-title-multiple', { count: outages.length });

            const message = isSingle ? firstOutage.message : null;

            const description =
                message ||
                t('provider-connections.bank-outage-description', {
                    names: formatList(
                        outages.map((outage) => (isSingle ? outage.name : getConnectorLabel(outage))),
                        { limit: 3, locale },
                    ),
                    interpolation: NO_ESCAPE,
                });

            return { title, description };
        },
        [t, locale, getTitle],
    );

    // A connector with problems still works: only the connections show it, without a banner.
    const getProblemsTexts = useCallback(
        (connector) => ({
            title: getTitle('bank-problems-title', connector),
            description: connector.message || t('provider-connections.bank-problems-description'),
        }),
        [t, getTitle],
    );

    return {
        getOutageTexts,
        getProblemsTexts,
        helpLabel: t('provider-connections.bank-outage-help'),
        helpLink: DOCUMENTATION_LINKS.BANK_OUTAGES[i18n.language] ?? DOCUMENTATION_LINKS.BANK_OUTAGES.ES,
    };
}
